import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, MapPin, Mail, Facebook, Linkedin } from 'lucide-react'
import { adminApi } from '../lib/api/admin'

interface SiteInfo {
  location?: string
  feedbackEmail?: string
  facebookUrl?: string
  tiktokUrl?: string
  xUrl?: string
  linkedinUrl?: string
}

export default function AboutUs() {
  const navigate = useNavigate()
  const [siteInfo, setSiteInfo] = useState<SiteInfo>({})

  useEffect(() => {
    adminApi.getSiteInfo().then(setSiteInfo).catch(() => {})
  }, [])

  return (
    <div className="landing" style={{ minHeight: '100vh' }}>

      {/* Header */}
      <header className="landing-header">
        <div className="landing-container">
          <div className="header-brand" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src="/InterviewDressRehearsal_LOGO.png" alt="Interview Dress Rehearsal" className="header-logo" />
            <div className="header-name-wrap">
              <span className="header-name-full">Interview Dress Rehearsal</span>
            </div>
          </div>
          <nav className="header-nav">
            <button className="btn-outline-light" onClick={() => navigate('/signin')}>Sign In</button>
            <button className="btn-cta" onClick={() => navigate('/signup')}>Get Started <ArrowRight size={16} /></button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ padding: '80px 0', background: '#f9fafb', minHeight: 'calc(100vh - 144px)' }}>
        <div className="landing-container" style={{ maxWidth: 760 }}>

          <div style={{ marginBottom: 48, textAlign: 'center' }}>
            <img src="/InterviewDressRehearsal_LOGO.png" alt="Interview Dress Rehearsal" style={{ width: 80, height: 80, borderRadius: '50%', marginBottom: 20 }} />
            <h1 style={{ fontSize: 36, fontWeight: 800, color: '#1f2937', marginBottom: 12 }}>About Interview Dress Rehearsal</h1>
            <p style={{ fontSize: 18, color: '#4b5563', lineHeight: 1.7 }}>
              We help job seekers walk into interviews with confidence — not anxiety.
            </p>
          </div>

          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 40, marginBottom: 24 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', marginBottom: 16 }}>Our Mission</h2>
            <p style={{ fontSize: 16, color: '#374151', lineHeight: 1.8, marginBottom: 12 }}>
              Interview Dress Rehearsal is a platform dedicated to helping professionals at every career stage practice and refine their interview skills through live, 1-on-1 mock interviews with experienced interviewers.
            </p>
            <p style={{ fontSize: 16, color: '#374151', lineHeight: 1.8 }}>
              We believe that preparation is the difference between a good candidate and a great one. Our sessions give you a safe space to make mistakes, get honest feedback, and show up as your best self when it matters most.
            </p>
          </div>

          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 40, marginBottom: 24 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', marginBottom: 20 }}>Contact & Connect</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {siteInfo.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, background: '#f3f4f6', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2d3748', flexShrink: 0 }}>
                    <MapPin size={20} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 15 }}>Location</div>
                    <div style={{ color: '#6b7280', fontSize: 14 }}>{siteInfo.location}</div>
                  </div>
                </div>
              )}

              {siteInfo.feedbackEmail && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, background: '#f3f4f6', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2d3748', flexShrink: 0 }}>
                    <Mail size={20} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 15 }}>Send Feedback</div>
                    <a href={`mailto:${siteInfo.feedbackEmail}`} style={{ color: '#2d3748', fontSize: 14, textDecoration: 'underline' }}>
                      {siteInfo.feedbackEmail}
                    </a>
                  </div>
                </div>
              )}

              {siteInfo.facebookUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, background: '#1877f2', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                    <Facebook size={20} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 15 }}>Facebook</div>
                    <a href={siteInfo.facebookUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2d3748', fontSize: 14, textDecoration: 'underline' }}>
                      {siteInfo.facebookUrl}
                    </a>
                  </div>
                </div>
              )}

              {siteInfo.tiktokUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, background: '#000', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, fontSize: 18, fontWeight: 700 }}>
                    T
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 15 }}>TikTok</div>
                    <a href={siteInfo.tiktokUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2d3748', fontSize: 14, textDecoration: 'underline' }}>
                      {siteInfo.tiktokUrl}
                    </a>
                  </div>
                </div>
              )}

              {siteInfo.xUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, background: '#000', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 15 }}>X (Twitter)</div>
                    <a href={siteInfo.xUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2d3748', fontSize: 14, textDecoration: 'underline' }}>
                      {siteInfo.xUrl}
                    </a>
                  </div>
                </div>
              )}

              {siteInfo.linkedinUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, background: '#0077b5', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                    <Linkedin size={20} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 15 }}>LinkedIn</div>
                    <a href={siteInfo.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2d3748', fontSize: 14, textDecoration: 'underline' }}>
                      {siteInfo.linkedinUrl}
                    </a>
                  </div>
                </div>
              )}

              {!siteInfo.location && !siteInfo.feedbackEmail && !siteInfo.facebookUrl && !siteInfo.tiktokUrl && !siteInfo.xUrl && !siteInfo.linkedinUrl && (
                <p style={{ color: '#9ca3af', fontSize: 14 }}>Contact information coming soon.</p>
              )}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button className="btn-cta btn-lg" onClick={() => navigate('/signup')}>
              Get Started <ArrowRight size={20} />
            </button>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/InterviewDressRehearsal_LOGO.png" alt="Interview Dress Rehearsal" className="footer-logo" />
              <span>Interview Dress Rehearsal</span>
            </div>
            <p className="footer-copy">&copy; {new Date().getFullYear()} Interview Dress Rehearsal. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  )
}
